(function () {
  if (window.CustoGiftsProductV9) return;
  window.CustoGiftsProductV9 = true;

  const variantInstances = new WeakMap();
  const galleryInstances = new WeakMap();
  const stickyInstances = new WeakMap();
  const recipientInstances = new WeakMap();

  function textElement(tagName, className, text) {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    element.textContent = text;
    return element;
  }

  function visuallyHidden(text) {
    return textElement('span', 'visually-hidden', text);
  }

  function normalizedHeading(text) {
    return String(text || '')
      .replace(/\s+/g, ' ')
      .replace(/[\s:]+$/, '')
      .trim()
      .toLowerCase();
  }

  function firstTextNode(element) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      if (node.data.trim()) return node;
      node = walker.nextNode();
    }
    return null;
  }

  function enhanceDescriptionListItem(item) {
    const directStrong = [...item.children].find((child) => child.tagName === 'STRONG');
    if (directStrong && /^[^:]{2,36}:\s*$/.test(directStrong.textContent || '')) {
      directStrong.textContent = directStrong.textContent.replace(/:\s*$/, '');
      directStrong.classList.add('product__description-spec-label');
    } else if (!directStrong) {
      const textNode = firstTextNode(item);
      const match = textNode?.data.match(/^\s*([^:]{2,36}):\s*/);
      if (match) {
        const label = document.createElement('strong');
        label.className = 'product__description-spec-label';
        label.textContent = match[1].trim();
        textNode.replaceWith(label, document.createTextNode(textNode.data.slice(match[0].length)));
      }
    }

    const textLength = item.textContent.replace(/\s+/g, ' ').trim().length;
    if (textLength > 118 || item.querySelectorAll('br').length > 1) {
      item.classList.add('product__description-spec-item--wide');
    }
  }

  function enhanceProductDescription(description) {
    if (description.dataset.cgDescriptionEnhanced === 'true') return;

    description.querySelectorAll('p').forEach((paragraph) => {
      if (!paragraph.textContent.trim() && !paragraph.querySelector('img, video, iframe, table')) {
        paragraph.classList.add('product__description-spacer');
      }
    });

    let detailsLabel = null;
    description.querySelectorAll('p, h2, h3, h4, h5, strong').forEach((element) => {
      const heading = normalizedHeading(element.textContent);
      let headingElement = element;
      if (element.tagName === 'STRONG') {
        const parent = element.parentElement;
        if (parent && normalizedHeading(parent.textContent) === heading) headingElement = parent;
      }
      if (heading === 'product details' || heading === 'product detail') {
        headingElement.classList.add('product__description-section-label');
        detailsLabel = detailsLabel || headingElement;
      } else if (heading === 'message') {
        headingElement.classList.add('product__description-message-label');
      }
    });

    const lists = [...description.querySelectorAll('ul')].filter((list) => !list.parentElement?.closest('li'));
    let specificationList = null;
    if (detailsLabel) {
      specificationList = lists.find((list) => (
        detailsLabel.compareDocumentPosition(list) & Node.DOCUMENT_POSITION_FOLLOWING
      ));
    }
    if (!specificationList && lists.length) specificationList = lists[lists.length - 1];

    lists.forEach((list) => {
      if (list === specificationList) {
        list.classList.add('product__description-spec-list');
        [...list.children].filter((item) => item.tagName === 'LI').forEach(enhanceDescriptionListItem);
      } else {
        list.classList.add('product__description-note-list');
      }
    });

    description.dataset.cgDescriptionEnhanced = 'true';
  }

  function enhanceProductDescriptions(scope = document) {
    rootsWithin(scope, '.main-product .product__description').forEach(enhanceProductDescription);
  }

  function enhanceAccordionLinkRows(scope = document) {
    rootsWithin(scope, '.main-product .product__accordion-content').forEach((content) => {
      const paragraphs = [...content.children].filter((child) => child.tagName === 'P');
      for (let index = paragraphs.length - 1; index >= 0; index -= 1) {
        if (paragraphs[index].querySelector('a')) {
          paragraphs[index].classList.add('product__accordion-links');
          break;
        }
      }
    });
  }

  function updateVariantPrice(priceRoot, variant, showBadges, showSavings) {
    const container = document.createElement('div');
    container.className = 'price__container';
    priceRoot.classList.toggle('price--sold-out', !variant.available);
    priceRoot.classList.toggle('price--on-sale', Boolean(variant.on_sale));
    priceRoot.dataset.cgPriceState = variant.on_sale ? 'sale' : 'regular';

    if (variant.on_sale) {
      const sale = document.createElement('span');
      sale.className = 'price__sale';
      sale.append(
        visuallyHidden('Sale price'),
        textElement('span', 'price-item price-item--sale', variant.formatted_price),
      );
      sale.querySelector('.price-item--sale')?.setAttribute('data-cg-price-current', '');
      const compare = document.createElement('span');
      compare.className = 'price__compare';
      compare.append(
        visuallyHidden('Regular price'),
        textElement('s', 'price-item price-item--compare', variant.formatted_compare_at_price),
      );
      compare.querySelector('.price-item--compare')?.setAttribute('data-cg-price-compare', '');
      sale.append(compare);
      container.append(sale);
      if (showBadges && showSavings && Number(variant.savings_percentage) > 0) {
        const saving = textElement('span', 'price__saving', 'Save ' + variant.savings_percentage + '%');
        saving.setAttribute('aria-label', 'Save ' + variant.savings_percentage + ' percent');
        saving.setAttribute('data-cg-price-saving', '');
        container.append(saving);
      }
    } else {
      const regular = document.createElement('span');
      regular.className = 'price__regular';
      regular.append(
        visuallyHidden('Regular price'),
        textElement('span', 'price-item', variant.formatted_price),
      );
      regular.querySelector('.price-item')?.setAttribute('data-cg-price-current', '');
      container.append(regular);
    }

    if (variant.formatted_unit_price && variant.unit_price_reference_unit) {
      const unitPrice = document.createElement('small');
      unitPrice.className = 'unit-price';
      unitPrice.append(visuallyHidden('Unit price'));
      unitPrice.append(document.createTextNode(variant.formatted_unit_price));
      const separator = textElement('span', '', '/');
      separator.setAttribute('aria-hidden', 'true');
      unitPrice.append(separator);
      if (Number(variant.unit_price_reference_value) !== 1) {
        unitPrice.append(document.createTextNode(String(variant.unit_price_reference_value)));
      }
      unitPrice.append(document.createTextNode(String(variant.unit_price_reference_unit)));
      container.append(unitPrice);
    }

    const additions = [container];
    if (showBadges) {
      if (!variant.available) {
        additions.push(textElement('span', 'badge price__badge-sold-out', 'Sold out'));
      } else if (variant.on_sale) {
        additions.push(textElement('span', 'badge price__badge-sale', 'On sale'));
      }
    }
    priceRoot.replaceChildren(...additions);
  }

  class VariantPicker {
    constructor(root) {
      this.root = root;
      this.section = root.closest('[data-section-id]');
      this.idInput = root.querySelector('[data-variant-id-input]');
      this.status = root.querySelector('[data-variant-status]');
      this.variantData = root.querySelector('[data-product-variants]');
      try {
        var parsedVariants = this.variantData ? JSON.parse(this.variantData.textContent) : [];
        this.variants = Array.isArray(parsedVariants) ? parsedVariants : [];
      } catch {
        this.variants = [];
      }
      this.onChange = this.onChange.bind(this);
      root.addEventListener('change', this.onChange);
      this.syncAvailability();
      this.syncSelectionLabels();
    }

    selectedValues() {
      return [...this.root.querySelectorAll('[data-option-value]:checked')]
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((input) => input.value);
    }

    syncSelectionLabels() {
      this.root.querySelectorAll('[data-option-position]').forEach((group) => {
        const selected = group.querySelector('[data-option-value]:checked');
        const label = group.querySelector('[data-cg-option-selection]');
        /* "Default Title" la chuoi Shopify tu dat cho san pham mot bien the, khong
           phai lua chon khach chon. Liquid da doi nhan hien thi thanh "Standard"
           o ca legend lan pill (variant-picker.liquid); doan nay ghi de len legend
           moi lan doi lua chon nen phai doi CUNG KIEU, khong thi chu do quay lai
           ngay cu bam dau tien. Chi doi phan NHIN THAY — selected.value van la gia
           tri that de doi chieu voi variant.options. */
        if (selected && label) {
          label.textContent = selected.value.trim().toLowerCase() === 'default title'
            ? 'Standard'
            : selected.value;
        }
      });
    }

    syncAvailability() {
      const groups = [...this.root.querySelectorAll('.product-form__option[data-option-position]')]
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
      const selected = this.selectedValues();

      groups.forEach((group) => {
        const groupIndex = Number(group.dataset.optionPosition) - 1;
        const inputs = [...group.querySelectorAll('[data-option-value]')];
        inputs.forEach((input) => {
          const prefix = selected.slice(0, groupIndex);
          const matches = this.variants.filter((variant) => (
            prefix.every((value, index) => variant.options[index] === value)
            && variant.options[groupIndex] === input.value
          ));
          const exists = matches.length > 0;
          const hasAvailable = matches.some((variant) => variant.available);
          input.disabled = !exists;
          const pill = input.closest('.product-form__option-pill');
          pill?.classList.toggle('is-unavailable', !exists);
          pill?.classList.toggle('is-sold-out', exists && !hasAvailable);
        });

        const checked = inputs.find((input) => input.checked);
        if (!checked || checked.disabled) {
          const replacement = inputs.find((input) => !input.disabled && !input.closest('.product-form__option-pill')?.classList.contains('is-sold-out'))
            || inputs.find((input) => !input.disabled);
          if (replacement) replacement.checked = true;
        }

        selected[groupIndex] = inputs.find((input) => input.checked)?.value;
      });
    }

    onChange(event) {
      if (!event.target.matches('[data-option-value]')) return;
      this.syncAvailability();
      this.syncSelectionLabels();
      const values = this.selectedValues();
      const variant = this.variants.find((candidate) => candidate.options.every((option, index) => option === values[index]));
      this.applyVariant(variant);
    }

    applyVariant(variant) {
      if (!variant) {
        if (this.status) this.status.textContent = 'This combination is unavailable.';
        this.setSubmitState(false, 'Unavailable');
        return;
      }

      if (this.idInput) this.idInput.value = variant.id;
      this.root.dataset.selectedVariantId = variant.id;
      if (this.status) {
        const selectedTitle = variant.title && variant.title !== 'Default Title' ? variant.title + '. ' : '';
        const availability = variant.available ? (variant.inventory_message || 'Available') : 'Sold out';
        this.status.textContent = selectedTitle + variant.formatted_price + '. ' + availability;
      }
      this.setSubmitState(Boolean(variant.available), variant.available ? 'Add to cart' : 'Sold out');
      this.section?.querySelectorAll('[data-cg-personalization-adapter]').forEach((adapter) => {
        adapter.dataset.variantId = variant.id;
      });

      const url = new URL(this.root.dataset.productUrl, document.baseURI || window.location.href);
      url.searchParams.set('variant', variant.id);
      try {
        window.history.replaceState({}, '', url.toString());
      } catch {}
      this.updatePrice(variant);
      this.updateInventory(variant);
      this.updateMetadata(variant);
      this.updateGallery(variant);
      this.root.dispatchEvent(new CustomEvent('custogifts:variant:change', {
        bubbles: true,
        detail: { variant },
      }));
    }

    setSubmitState(enabled, label) {
      this.section?.querySelectorAll('[data-cg-product-submit]').forEach((submit) => {
        submit.disabled = !enabled;
        submit.textContent = label;
      });
      this.section?.querySelectorAll('.shopify-payment-button').forEach((button) => {
        button.hidden = !enabled;
      });
    }

    updatePrice(variant) {
      this.section?.querySelectorAll('[data-cg-price]').forEach((priceRoot) => {
        const commerce = priceRoot.closest('.product__commerce');
        const showBadges = Boolean(commerce);
        const showSavings = commerce?.dataset.cgShowSavings !== 'false';
        updateVariantPrice(priceRoot, variant, showBadges, showSavings);
      });
    }

    updateInventory(variant) {
      this.section?.querySelectorAll('[data-cg-product-inventory]').forEach((inventory) => {
        inventory.classList.toggle('is-available', Boolean(variant.available));
        inventory.classList.toggle('is-unavailable', !variant.available);
        inventory.dataset.availability = variant.available ? 'available' : 'unavailable';
        let message = inventory.querySelector('p');
        if (!message) {
          message = document.createElement('p');
          inventory.append(message);
        }
        message.textContent = variant.inventory_message;
      });
    }

    updateMetadata(variant) {
      const metadata = this.section?.querySelector('[data-cg-product-meta]');
      if (!metadata) return;

      const skuRow = metadata.querySelector('[data-cg-product-sku-row]');
      const sku = metadata.querySelector('[data-cg-product-sku]');
      if (skuRow && sku) {
        sku.textContent = variant.sku || '';
        skuRow.hidden = !variant.sku;
      }

      const selected = metadata.querySelector('[data-cg-product-selected]');
      if (selected) selected.textContent = variant.title || '';
      metadata.hidden = [...metadata.children].every((row) => row.hidden);
    }

    updateGallery(variant) {
      if (!variant.featured_media_id || !this.section) return;
      const galleryRoot = this.section.querySelector('[data-cg-product-gallery]');
      const gallery = galleryRoot ? galleryInstances.get(galleryRoot) : null;
      if (!gallery) return;
      const mediaIndex = gallery.items.findIndex(
        (item) => String(item.dataset.mediaId) === String(variant.featured_media_id),
      );
      if (mediaIndex >= 0) gallery.setActive(mediaIndex, { scroll: true, scrollThumbnail: true });
    }

    destroy() {
      this.root.removeEventListener('change', this.onChange);
    }
  }

  class ProductGallery {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-cg-gallery-track]');
      this.items = [...root.querySelectorAll('[data-cg-gallery-item]')];
      this.thumbs = [...root.querySelectorAll('[data-cg-gallery-thumb]')];
      this.current = root.querySelector('[data-cg-gallery-current]');
      this.dialog = root.querySelector('[data-cg-gallery-lightbox]');
      this.lightboxImage = root.querySelector('[data-cg-lightbox-image]');
      this.previousFocus = null;
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.scrollFrame = null;
      this.activeIndex = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active')));
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onTrackScroll = this.onTrackScroll.bind(this);
      this.onModeChange = this.onModeChange.bind(this);
      this.onDialogClose = this.onDialogClose.bind(this);
      this.onMediaActivate = this.onMediaActivate.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.coarsePointer = window.matchMedia('(pointer: coarse)');
      this.tapStart = null;

      root.addEventListener('click', this.onClick);
      root.addEventListener('dblclick', this.onMediaActivate);
      root.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.dialog?.addEventListener('close', this.onDialogClose);
      root.addEventListener('keydown', this.onKeydown);
      this.track?.addEventListener('scroll', this.onTrackScroll, { passive: true });
      if (this.mobileQuery.addEventListener) this.mobileQuery.addEventListener('change', this.onModeChange);
      else this.mobileQuery.addListener(this.onModeChange);

      this.setActive(this.activeIndex);
      if (this.mobileQuery.matches && this.activeIndex > 0) {
        window.requestAnimationFrame(() => this.scrollToItem(this.activeIndex, 'auto'));
      }
    }

    onClick(event) {
      const close = event.target.closest('[data-cg-lightbox-close]');
      if (close || event.target === this.dialog) {
        this.closeLightbox();
        return;
      }

      const zoom = event.target.closest('[data-cg-gallery-zoom]');
      if (zoom && this.root.contains(zoom)) {
        this.openLightbox(zoom);
        return;
      }

      /* Màn cảm ứng: CHẠM một cái vào ảnh là mở ảnh lớn.
         Không dùng bấm đúp ở đây — trên di động thao tác đó đã thuộc về trình
         duyệt (chạm hai lần để phóng trang), giành lấy nó là phá thói quen sẵn
         có. Mà 90% khách của store ở trên di động, nên đây mới là đường chính.

         Chốt chặn vuốt: nếu ngón tay đã DI CHUYỂN quá 10px kể từ lúc chạm
         xuống thì đó là cử chỉ vuốt chuyển ảnh, không phải chạm mở. Thiếu chốt
         này thì mỗi lần khách vuốt xem ảnh tiếp theo lại bật hộp ảnh lớn. */
      if (this.coarsePointer.matches) {
        const anh = event.target.closest('[data-cg-gallery-item] .product__media-image');
        if (anh && this.root.contains(anh)) {
          const s = this.tapStart;
          const doDi = s ? Math.hypot(event.clientX - s.x, event.clientY - s.y) : 0;
          if (doDi <= 10) {
            this.onMediaActivate(event);
            return;
          }
        }
      }

      const thumb = event.target.closest('[data-cg-gallery-thumb]');
      if (thumb && this.root.contains(thumb)) {
        this.setActive(Number(thumb.dataset.mediaPosition), { scroll: true, scrollThumbnail: true });
        return;
      }

      if (event.target.closest('[data-cg-gallery-previous]')) {
        this.setActive((this.activeIndex - 1 + this.items.length) % this.items.length, { scroll: true, scrollThumbnail: true });
      } else if (event.target.closest('[data-cg-gallery-next]')) {
        this.setActive((this.activeIndex + 1) % this.items.length, { scroll: true, scrollThumbnail: true });
      }
    }

    onPointerDown(event) {
      this.tapStart = { x: event.clientX, y: event.clientY };
    }

    /* Mở ảnh lớn từ CHÍNH tấm ảnh. Gắn vào dblclick cho chuột, và được gọi lại
       từ onClick cho màn cảm ứng.

       Vẫn đi qua đúng cái nút cũ làm "trigger" chứ không nhân bản dữ liệu:
       openLightbox đọc data-full-image, -srcset, -sizes, -alt, -width, -height
       từ phần tử được truyền vào. Chép sáu thuộc tính đó sang thẻ ảnh là tạo ra
       hai nguồn sự thật cho cùng một thứ, và chúng sẽ lệch nhau ở lần sửa sau. */
    onMediaActivate(event) {
      const anh = event.target.closest('[data-cg-gallery-item] .product__media-image');
      if (!anh || !this.root.contains(anh)) return;
      const o = anh.closest('[data-cg-gallery-item]');
      const nut = o && o.querySelector('[data-cg-gallery-zoom]');
      if (nut) this.openLightbox(nut);
    }

    openLightbox(trigger) {
      const source = trigger.dataset.fullImage;
      if (!source || !this.dialog || !this.lightboxImage) return;
      this.previousFocus = trigger;
      this.lightboxImage.alt = trigger.dataset.fullImageAlt || '';
      this.lightboxImage.width = Number(trigger.dataset.fullImageWidth) || 1;
      this.lightboxImage.height = Number(trigger.dataset.fullImageHeight) || 1;
      this.lightboxImage.srcset = trigger.dataset.fullImageSrcset || '';
      this.lightboxImage.sizes = trigger.dataset.fullImageSizes || '(max-width: 749px) calc(100vw - 32px), min(1180px, calc(100vw - 64px))';
      this.lightboxImage.src = source;
      if (window.CustoGiftsOverlay) window.CustoGiftsOverlay.activate(this.dialog);
      else document.body.classList.add('cg-overlay-open');
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      this.dialog.querySelector('[data-cg-lightbox-close]')?.focus();
    }

    closeLightbox() {
      if (!this.dialog?.hasAttribute('open')) return;
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else {
        this.dialog.removeAttribute('open');
        this.onDialogClose();
      }
    }

    onDialogClose() {
      if (window.CustoGiftsOverlay) window.CustoGiftsOverlay.deactivate(this.dialog);
      else document.body.classList.remove('cg-overlay-open');
      this.lightboxImage?.removeAttribute('src');
      this.lightboxImage?.removeAttribute('srcset');
      this.lightboxImage?.removeAttribute('sizes');
      if (this.lightboxImage) {
        this.lightboxImage.width = 1;
        this.lightboxImage.height = 1;
      }
      this.previousFocus?.focus();
      this.previousFocus = null;
    }

    onKeydown(event) {
      const thumb = event.target.closest('[data-cg-gallery-thumb]');
      if (!thumb || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();

      let index = Number(thumb.dataset.mediaPosition);
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = this.thumbs.length - 1;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') index = (index - 1 + this.thumbs.length) % this.thumbs.length;
      else index = (index + 1) % this.thumbs.length;

      this.setActive(index, { scroll: true, scrollThumbnail: true });
      this.thumbs[index]?.focus();
    }

    onTrackScroll() {
      if (!this.mobileQuery.matches || this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        const trackCenter = this.track.scrollLeft + (this.track.clientWidth / 2);
        let closestIndex = this.activeIndex;
        let closestDistance = Number.POSITIVE_INFINITY;
        this.items.forEach((item, index) => {
          const itemCenter = item.offsetLeft + (item.offsetWidth / 2);
          const distance = Math.abs(itemCenter - trackCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });
        if (closestIndex !== this.activeIndex) this.setActive(closestIndex, { scrollThumbnail: true });
      });
    }

    onModeChange() {
      this.setActive(this.activeIndex, { scrollThumbnail: true });
      if (this.mobileQuery.matches) window.requestAnimationFrame(() => this.scrollToItem(this.activeIndex, 'auto'));
    }

    scrollToItem(index, behavior) {
      const item = this.items[index];
      if (!item || !this.track) return;
      this.track.scrollTo({
        left: item.offsetLeft,
        behavior: this.reducedMotionQuery.matches ? 'auto' : behavior,
      });
    }

    scrollThumbnailToItem(index, behavior) {
      const thumb = this.thumbs[index];
      const rail = thumb?.closest('[data-cg-gallery-thumbs]');
      if (!thumb || !rail) return;

      const maxLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const maxTop = Math.max(0, rail.scrollHeight - rail.clientHeight);
      if (maxLeft === 0 && maxTop === 0) return;

      rail.scrollTo({
        left: maxLeft > 0
          ? Math.min(maxLeft, Math.max(0, thumb.offsetLeft - ((rail.clientWidth - thumb.offsetWidth) / 2)))
          : rail.scrollLeft,
        top: maxTop > 0
          ? Math.min(maxTop, Math.max(0, thumb.offsetTop - ((rail.clientHeight - thumb.offsetHeight) / 2)))
          : rail.scrollTop,
        behavior: this.reducedMotionQuery.matches ? 'auto' : behavior,
      });
    }

    setActive(index, options = {}) {
      if (!this.items.length) return;
      const nextIndex = Math.max(0, Math.min(index, this.items.length - 1));
      this.activeIndex = nextIndex;

      this.items.forEach((item, itemIndex) => {
        item.classList.toggle('is-active', itemIndex === nextIndex);
        if (itemIndex !== nextIndex) item.querySelectorAll('video').forEach((video) => video.pause());
      });
      this.thumbs.forEach((thumb, thumbIndex) => {
        const isActive = thumbIndex === nextIndex;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-pressed', String(isActive));
        if (isActive) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (this.current) this.current.textContent = String(nextIndex + 1);
      if (options.scroll && this.mobileQuery.matches) this.scrollToItem(nextIndex, 'smooth');
      if (options.scrollThumbnail) this.scrollThumbnailToItem(nextIndex, 'smooth');
    }

    destroy() {
      this.closeLightbox();
      this.root.removeEventListener('click', this.onClick);
      this.root.removeEventListener('keydown', this.onKeydown);
      this.dialog?.removeEventListener('close', this.onDialogClose);
      this.track?.removeEventListener('scroll', this.onTrackScroll);
      if (this.mobileQuery.removeEventListener) this.mobileQuery.removeEventListener('change', this.onModeChange);
      else this.mobileQuery.removeListener(this.onModeChange);
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }
  }

  class GiftCardRecipient {
    constructor(root) {
      this.root = root;
      this.toggle = root.querySelector('[data-gift-card-recipient-toggle]');
      this.fields = root.querySelector('[data-gift-card-recipient-fields]');
      this.email = root.querySelector('[data-gift-card-recipient-email]');
      this.onChange = this.sync.bind(this);
      this.toggle?.addEventListener('change', this.onChange);
      this.sync();
    }

    sync() {
      const enabled = Boolean(this.toggle?.checked);
      this.toggle?.setAttribute('aria-expanded', String(enabled));
      if (this.fields) this.fields.hidden = !enabled;
      this.root.querySelectorAll('[data-gift-card-recipient-field]').forEach((field) => {
        field.disabled = !enabled;
      });
      if (this.email) this.email.required = enabled;
    }

    destroy() {
      this.toggle?.removeEventListener('change', this.onChange);
    }
  }

  class StickyPurchase {
    constructor(root) {
      this.root = root;
      this.section = root.closest('[data-section-id]');
      this.primarySubmit = null;
      this.observer = null;
      this.frame = null;
      this.primaryPassed = false;
      this.sectionVisible = true;
      this.onViewportChange = this.onViewportChange.bind(this);
      this.refresh();
    }

    updateVisibility() {
      this.root.hidden = !(this.primaryPassed && this.sectionVisible);
    }

    onViewportChange() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        if (!this.primarySubmit || !document.contains(this.primarySubmit) || !this.section) {
          this.refresh();
          return;
        }
        const rect = this.primarySubmit.getBoundingClientRect();
        const sectionRect = this.section.getBoundingClientRect();
        this.primaryPassed = rect.bottom <= 0;
        this.sectionVisible = sectionRect.bottom > 0 && sectionRect.top < window.innerHeight;
        this.updateVisibility();
      });
    }

    refresh() {
      this.disconnect();
      this.primarySubmit = this.section?.querySelector('.product-form__buttons [data-cg-product-submit]') || null;
      if (!this.primarySubmit) {
        this.primaryPassed = false;
        this.updateVisibility();
        return;
      }

      const rect = this.primarySubmit.getBoundingClientRect();
      const sectionRect = this.section.getBoundingClientRect();
      this.primaryPassed = rect.bottom <= 0;
      this.sectionVisible = sectionRect.bottom > 0 && sectionRect.top < window.innerHeight;
      this.updateVisibility();

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.target === this.primarySubmit) {
              this.primaryPassed = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
            } else if (entry.target === this.section) {
              this.sectionVisible = entry.isIntersecting;
            }
          });
          this.updateVisibility();
        }, { threshold: 0.01 });
        this.observer.observe(this.primarySubmit);
        if (this.section) this.observer.observe(this.section);
      } else {
        window.addEventListener('scroll', this.onViewportChange, { passive: true });
        window.addEventListener('resize', this.onViewportChange);
        this.onViewportChange();
      }
    }

    disconnect() {
      this.observer?.disconnect();
      this.observer = null;
      window.removeEventListener('scroll', this.onViewportChange);
      window.removeEventListener('resize', this.onViewportChange);
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    destroy() {
      this.disconnect();
      this.primaryPassed = false;
      this.sectionVisible = false;
      this.updateVisibility();
    }
  }

  function rootsWithin(scope, selector) {
    const roots = [];
    if (scope instanceof Element && scope.matches(selector)) roots.push(scope);
    scope.querySelectorAll?.(selector).forEach((root) => roots.push(root));
    return roots;
  }

  function refreshStickyPurchases(scope = document) {
    rootsWithin(scope, '[data-cg-sticky-purchase]').forEach((root) => {
      const existing = stickyInstances.get(root);
      if (existing) existing.refresh();
      else stickyInstances.set(root, new StickyPurchase(root));
    });
  }

  function destroyGalleryInstances(scope) {
    rootsWithin(scope, '[data-cg-product-gallery]').forEach((root) => {
      galleryInstances.get(root)?.destroy();
      galleryInstances.delete(root);
    });
  }

  function initialize(scope = document) {
    rootsWithin(scope, '[data-cg-variant-picker]').forEach((root) => {
      if (variantInstances.has(root)) return;
      variantInstances.set(root, new VariantPicker(root));
    });
    rootsWithin(scope, '[data-cg-product-gallery]').forEach((root) => {
      if (galleryInstances.has(root)) return;
      galleryInstances.set(root, new ProductGallery(root));
    });
    rootsWithin(scope, '[data-gift-card-recipient]').forEach((root) => {
      if (recipientInstances.has(root)) return;
      recipientInstances.set(root, new GiftCardRecipient(root));
    });
    refreshStickyPurchases(scope);
    enhanceProductDescriptions(scope);
    enhanceAccordionLinkRows(scope);
  }

  window.CustoGiftsProductDescription = Object.freeze({ enhance: enhanceProductDescriptions });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    rootsWithin(event.target, '[data-cg-variant-picker]').forEach((root) => {
      variantInstances.get(root)?.destroy();
      variantInstances.delete(root);
    });
    destroyGalleryInstances(event.target);
    rootsWithin(event.target, '[data-gift-card-recipient]').forEach((root) => {
      recipientInstances.get(root)?.destroy();
      recipientInstances.delete(root);
    });
    rootsWithin(event.target, '[data-cg-sticky-purchase]').forEach((root) => {
      stickyInstances.get(root)?.destroy();
      stickyInstances.delete(root);
    });
  });
})();

/* Bậc tăng/giảm cho ô số lượng. Dùng stepUp/stepDown của chính trình duyệt để
   min/step trên input vẫn được tôn trọng, rồi phát sự kiện change — Custfy đọc
   input[name=quantity] nên phải báo cho nó biết giá trị đã đổi. */
(function () {
  'use strict';
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-cg-qty-step]');
    if (!btn) return;
    var input = btn.parentElement.querySelector('input[name="quantity"]');
    if (!input) return;
    var step = Number(btn.getAttribute('data-cg-qty-step'));
    if (step > 0) input.stepUp(); else input.stepDown();
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
})();


/* ── Giá mỗi cái ────────────────────────────────────────────────────────────
   KHÔNG tự tính từ variant.price. Đối tượng biến thể của theme này không mang
   price dạng số — nó chỉ có formatted_price ("$51.80"). Bản đầu em giả định có
   price nên điều kiện rớt im lặng và con số không bao giờ hiện.

   Và cũng không đi phân tích chuỗi "$51.80" ngược lại thành số: dấu phân cách
   nghìn, ký hiệu tiền tệ, vị trí ký hiệu đều đổi theo thị trường — store đang
   mở 27 nước, kiểu parse đó sẽ sai ở đâu đó mà không ai thấy.

   Liquid ĐÃ tính sẵn giá mỗi cái cho từng mức và in ra trong ô. Chỉ việc chép
   lại đúng con số đó. Không parse, không giả định tiền tệ, và luôn khớp tuyệt
   đối với thứ khách đang nhìn ngay bên dưới. */
(function () {
  var el = document.querySelector('[data-cg-unit-price]');
  var gs = document.querySelector('[data-cg-gift-set]');
  if (!el || !gs) return;

  function ve() {
    var chon = gs.querySelector('input:checked');
    var o = chon && chon.closest('.product-form__giftset-tier');
    var nEl = o && o.querySelector('.product-form__giftset-n');
    var eachEl = o && o.querySelector('.product-form__giftset-each');
    var n = nEl ? parseInt(nEl.textContent, 10) : 0;

    /* Một cái thì giá mỗi cái CHÍNH LÀ giá tổng — in ra là thừa. */
    if (!n || n < 2 || !eachEl) {
      el.hidden = true;
      el.textContent = '';
      return;
    }

    el.textContent = eachEl.textContent.replace(/s*eas*$/i, '').trim() + ' each';
    el.hidden = false;
  }

  /* Nghe cả hai: sự kiện đổi biến thể của theme, và change ngay trên nhóm radio
     phòng khi luồng kia đổi. Cả hai cùng gọi một hàm nên chạy hai lần vô hại. */
  var picker = document.querySelector('[data-cg-variant-picker]');
  if (picker) picker.addEventListener('custogifts:variant:change', ve);
  gs.addEventListener('change', ve);

  ve();
})();


/* ── Đồng hồ đếm ngược ưu đãi ───────────────────────────────────────────────
   Mốc kết thúc do Liquid truyền sang bằng giây epoch, nên không phụ thuộc múi
   giờ của store lẫn của máy khách.

   Hết giờ thì GỠ HẲN khối khỏi trang, không phải chỉ dừng số. Để lại một khối
   ưu đãi ghi 00:00:00 còn tệ hơn không có: nó vẫn chiếm chỗ và vẫn hứa hẹn
   một thứ đã hết. */
(function () {
  var el = document.querySelector('[data-cg-offer]');
  if (!el) return;

  var clock = el.querySelector('[data-cg-offer-clock]');
  var end = Number(el.getAttribute('data-end'));
  if (!clock || !end) return;

  function hai(n) { return n < 10 ? '0' + n : String(n); }

  function tick() {
    var con = end - Math.floor(Date.now() / 1000);
    if (con <= 0) {
      el.remove();
      return;
    }
    var ngay = Math.floor(con / 86400);
    var gio = Math.floor((con % 86400) / 3600);
    var phut = Math.floor((con % 3600) / 60);
    var giay = con % 60;

    /* Còn nhiều ngày thì đếm từng giây là vô nghĩa — không ai ngồi chờ 3 ngày.
       Giây chỉ có ích khi đã vào giờ chót. */
    clock.textContent = ngay > 0
      ? 'Ends in ' + ngay + 'd ' + hai(gio) + 'h ' + hai(phut) + 'm'
      : 'Ends in ' + hai(gio) + ':' + hai(phut) + ':' + hai(giay);

    setTimeout(tick, ngay > 0 ? 60000 : 1000);
  }

  tick();
})();


/* ── Thước tiến độ của khối cá nhân hoá ─────────────────────────────────────
   CHỈ ĐỌC DOM của Custfy, không đụng logic hay luồng add-to-cart của nó. Đếm
   .ant-form-item và xem cái nào đã có giá trị.

   Phải có MutationObserver: Custfy dựng form bằng JS sau khi trang tải xong,
   nên lúc đoạn này chạy lần đầu thường chưa có trường nào. Không quan sát thì
   thước đứng im ở 0 mãi — mà đó lại là kiểu hỏng im lặng khó thấy nhất.

   Đếm lại cả khi khách bấm/gõ: Custfy không phát sự kiện riêng nào ta bám được,
   nên nghe input/change/click ở mức document rồi lọc theo vùng. */
(function () {
  var wrap = document.querySelector('[data-cg-panel-progress]');
  var panel = document.querySelector('.product-form__personalization');
  if (!wrap || !panel) return;

  var chu = wrap.querySelector('[data-cg-progress-text]');
  var fill = wrap.querySelector('[data-cg-progress-fill]');

  function daDien(item) {
    /* '.image-item' chu KHONG 'img.image-item': SDK render <div class="image-item">
       boc <img> ben trong, khong bao gio gan class len chinh the <img>. Do tren
       trang that 25/08/2026: img.image-item khop 0 phan tu, .image-item khop 11.
       Bo chon anh cua Custfy cung khong co input that (xem custfy-widget-v59:341),
       nen nhanh input:checked cung truot — nghia la MOI truong o-chon-anh vinh
       vien bi dem la chua dien va thuoc tien do khong bao gio toi ALL SET. */
    var chon = item.querySelector('.image-item.active, .ant-select-selection-item, input:checked');
    if (chon) return true;
    var o = item.querySelector('input:not([type=radio]):not([type=checkbox]), textarea');
    return !!(o && o.value && o.value.trim());
  }

  function ve() {
    var items = panel.querySelectorAll('.ant-form-item');
    var tong = items.length;
    if (!tong) { wrap.hidden = true; return; }

    var xong = 0;
    for (var i = 0; i < tong; i++) { if (daDien(items[i])) xong++; }

    wrap.hidden = false;
    chu.textContent = xong === tong ? 'ALL SET' : xong + ' / ' + tong;
    fill.style.width = Math.round((xong / tong) * 100) + '%';
    wrap.classList.toggle('is-done', xong === tong);
  }

  var hen;
  function veSau() { clearTimeout(hen); hen = setTimeout(ve, 60); }

  ['input', 'change', 'click'].forEach(function (ev) {
    document.addEventListener(ev, function (e) {
      if (e.target && e.target.closest && e.target.closest('.product-form__personalization')) veSau();
    }, true);
  });

  new MutationObserver(veSau).observe(panel, { childList: true, subtree: true });

  ve();
})();
